import type { Tone } from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

export interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
}

export interface Modifier {
  id: number;
  name: string;
  code?: string;
  quantity?: number;
}

export interface OrderItem {
  id: number;
  order: number;
  menu_item: number;
  menu_item_name?: string;
  quantity: number;
  price: number;
  modifiers: number[];
  modifier_details?: Modifier[];
  note: string | null;
  created_at: string;
}

export interface Order {
  id: number;
  store: number;
  customer: Customer | null;
  customer_name: string;
  customer_phone: string;
  order_items: OrderItem[];
  total_price: number;
  total_price_without_taxes_and_fees: number;
  tax: number;
  is_fully_refunded: boolean;
  refunded_total: number | null;
  payment_id: string | null;
  currency?: string | null;
  status: string;
  payment_link: string;
  stripe_payment_intent_id: string | null;
  stripe_checkout_session_id: string | null;
  is_paid: boolean;
  pay_on_site: boolean;
  is_sent_to_aggregator: boolean;
  pickup_code: string | null;
  channel_order_id: string;
  delivery_address?: string | null;
  // Accept both older numeric order_type and newer string-based order_type values
  order_type?: number | string | null;
  delivery_fee_cents: number;
  service_fee_cents: number;
  dispatch_delivery_cost_cents: number;
  created_at: string;
  pickup_eta: string | null;
  updated_at: string;
}

export const ORDERS_PAGE_SIZE = 25;

export const DEFAULT_ORDERS_ORDERING = '-created_at';

/** Status views; the key is sent as `status` (the API ignores `All`). */
export const ORDER_STATUS_VIEWS = ['All', 'Paid', 'Canceled'] as const;
export type OrderStatusView = (typeof ORDER_STATUS_VIEWS)[number];

/** UI sort column -> API `ordering` field name */
export const ORDER_SORT_UI_TO_FIELD: Record<string, string> = {
  customer_id: 'customer',
  created_at: 'created_at',
  id: 'id',
  channel_order_id: 'channel_order_id',
  customer_name: 'customer_name',
  currency: 'currency',
  total_price: 'total_price',
};

export const ORDER_SORT_FIELD_TO_UI: Record<string, string> =
  Object.fromEntries(
    Object.entries(ORDER_SORT_UI_TO_FIELD).map(([k, v]) => [v, k]),
  );

export interface OrdersQueryResult {
  orders: Order[];
  totalCount: number;
}

export async function fetchOrdersPage(
  currentPage: number,
  itemsPerPage: number,
  query: string,
  ordering?: string,
  search?: string,
): Promise<OrdersQueryResult> {
  const response = await api.get(
    apiEndpoints.getOrders(currentPage, itemsPerPage, query, ordering, search),
  );

  if (!response?.ok) {
    throw new Error('Failed to fetch orders');
  }

  const data = await response.json();
  let ordersList: Order[] = [];
  let totalCount = 0;

  if (data.results && Array.isArray(data.results)) {
    ordersList = data.results;
    totalCount = data.count || data.total || 0;
  } else if (data.data && Array.isArray(data.data)) {
    ordersList = data.data;
    totalCount = data.count || data.total || 0;
  } else if (Array.isArray(data)) {
    ordersList = data;
    totalCount = data.length;
    console.warn('API returned plain array without count field');
  } else {
    console.error('Unexpected response format:', data);
  }

  return { orders: ordersList, totalCount };
}

export interface OrdersStatsResult {
  orderCount: number;
  orderValueTotal: number;
}

/** A failed count request reads as zero; the page never shows an error for it. */
export async function fetchOrderStats(): Promise<OrdersStatsResult> {
  const response = await api.get(apiEndpoints.getOrdersCount());
  if (!response?.ok) {
    return { orderCount: 0, orderValueTotal: 0 };
  }

  const data = await response.json();
  return {
    orderCount: data.count ?? 0,
    orderValueTotal:
      typeof data.total_order_amount === 'number'
        ? data.total_order_amount
        : Number(data.total_order_amount) || 0,
  };
}

/** `total_order_amount` from the count endpoint is in dollars, not cents. */
export function formatDollars(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(n);
}

export const getCurrencyCode = (currency?: string | null) =>
  (currency || 'USD').toUpperCase();

export const formatCurrencyFromCents = (
  cents?: number | null,
  currency?: string | null,
) => {
  const code = getCurrencyCode(currency);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((cents ?? 0) / 100);
  } catch {
    return `${code} ${((cents ?? 0) / 100).toFixed(2)}`;
  }
};

/**
 * Status → tone, on the shared chip palette: done is green, in progress is
 * amber, paid is accent, cancelled is danger. Matches only the exact strings
 * listed; anything else (including the API's `Canceled` and `Failed`) takes
 * the accent fallback.
 */
export const orderStatusTone = (status?: string): Tone =>
  status === 'Accepted' || status === 'Completed'
    ? 'success'
    : status === 'Pending' || status === 'Preparing'
      ? 'warning'
      : status === 'Paid'
        ? 'accent'
        : status === 'Cancelled' || status === 'Rejected'
          ? 'danger'
          : 'accent';

/** Status shown in the list: a missing status reads as Paid / Unpaid. */
export const listStatusLabel = (order: Order) =>
  order.status || (order.is_paid ? 'Paid' : 'Unpaid');

/** Status shown in the detail: a missing status reads as Paid / Unknown. */
export const detailStatusLabel = (order: Order) =>
  order.status || (order.is_paid ? 'Paid' : 'Unknown');

export type FulfillmentType = 'pickup' | 'delivery' | 'unknown';

export const FULFILLMENT_LABEL: Record<FulfillmentType, string> = {
  pickup: 'Pickup',
  delivery: 'Delivery',
  unknown: 'Unknown',
};

const explicitFulfillment = (order: any): FulfillmentType | null => {
  const raw = order.order_type;
  const normalized = (raw === null || raw === undefined ? '' : String(raw))
    .toLowerCase()
    .trim();
  if (raw === 1 || normalized === 'pickup') return 'pickup';
  if (raw === 2 || normalized === 'delivery') return 'delivery';
  return null;
};

const hasDeliveryCost = (order: any) =>
  (order.delivery_fee_cents ?? 0) > 0 ||
  (order.dispatch_delivery_cost_cents ?? 0) > 0;

/**
 * Type column: explicit `order_type`, then pickup code, then delivery costs.
 * Deliberately ignores the delivery address (the detail does not).
 */
export const listFulfillmentType = (order: any): FulfillmentType =>
  explicitFulfillment(order) ??
  (order.pickup_code
    ? 'pickup'
    : hasDeliveryCost(order)
      ? 'delivery'
      : 'unknown');

/** Detail: explicit `order_type`, then an address, pickup code, delivery costs. */
export const detailFulfillmentType = (order?: any): FulfillmentType => {
  if (!order) return 'unknown';
  const explicit = explicitFulfillment(order);
  if (explicit) return explicit;
  if (
    order.delivery_address ||
    order.deliveryAddress ||
    order.delivery_address_line_1
  ) {
    return 'delivery';
  }
  if (order.pickup_code) return 'pickup';
  if (hasDeliveryCost(order)) return 'delivery';
  return 'unknown';
};

export const formatDeliveryAddress = (order?: any): string => {
  if (!order) return '';

  const addressCandidate =
    order.delivery_address ??
    order.deliveryAddress ??
    order.delivery_address_obj ??
    order.delivery_location ??
    null;

  if (typeof addressCandidate === 'string') return addressCandidate;

  const obj =
    addressCandidate && typeof addressCandidate === 'object'
      ? addressCandidate
      : order;
  const street =
    obj.street ??
    obj.address ??
    obj.address_line_1 ??
    obj.addressLine1 ??
    obj.line1 ??
    obj.delivery_address_line_1 ??
    '';
  const city = obj.city ?? '';
  const state = obj.state ?? obj.province ?? '';
  const postal =
    obj.postal_code ??
    obj.zip ??
    obj.zip_code ??
    obj.postalCode ??
    obj.zipCode ??
    '';
  const country = obj.country ?? obj.country_code ?? '';

  return [street, city, state, postal, country]
    .map((v: any) => String(v || '').trim())
    .filter(Boolean)
    .join(', ');
};

export const itemCountLabel = (order: Order) =>
  order.order_items && order.order_items.length > 0
    ? `${order.order_items.length} ${
        order.order_items.length === 1 ? 'item' : 'items'
      }`
    : 'No items';
